// SPDX-License-Identifier: GPL-3.0-or-later

import { INTERVIEW_JSON_SCHEMA } from '../schemas';

export interface InterviewPromptInputs {
  profileText: string;
  jobTitle: string;
  companyName: string;
  jobUrl: string;
  jobDescription: string;
  interviewerName: string;
  interviewerRole?: string;
  interviewType?: string;
}

export const interviewSystemPrompt = `You are an interview prep coach. Produce a focused prep brief for a specific interview.

You output ONLY valid JSON matching exactly this TypeScript shape:

${INTERVIEW_JSON_SCHEMA}

Rules:
- No markdown, no code fences, no commentary — only the JSON.
- Use plain text only; do NOT include LaTeX or HTML.
- "likelyTopics": 3-6 topics. Each topic has 2-5 sample questions that match the role and stage.
- "yourQuestions": 4-8 questions that genuinely probe role fit (avoid generic "what's the culture like").
- "companyTalkingPoints": 3-5 substantive things to know about the company / its market.
- "recentNews": include ONLY if you can name a specific recent event with high confidence; otherwise omit. The renderer flags this section as needing verification.
- Tune content to the interviewer's role: technical depth for engineering interviewers, scope/strategy for managers, narrative-fit for founders, etc.`;

export function interviewUserPrompt(inputs: InterviewPromptInputs): string {
  return `<profile>
${inputs.profileText.trim() || '(profile is empty)'}
</profile>

<job>
Title: ${inputs.jobTitle}
Company: ${inputs.companyName}
URL: ${inputs.jobUrl}

${inputs.jobDescription.trim() || '(no description recorded)'}
</job>

<interview>
Interviewer: ${inputs.interviewerName}${inputs.interviewerRole ? ` (${inputs.interviewerRole})` : ''}
Type: ${inputs.interviewType ?? 'unspecified — infer from the role'}
</interview>

Produce the prep brief as JSON matching the schema. Output only the JSON.`;
}
